import api from './api';

export interface GoggleModel {
  id: string;
  name: string;
  description: string | null;
}

export interface Order {
  id: string;
  orderNumber: string;
  dealerId: string;
  goggleModelId: string;
  glassType: string;
  bandType: string;
  prescriptionImageMimeType: string | null;
  productType: 'CLIP' | 'COMPLETE_GLASSES';
  deliveryType: 'SHOP' | 'CUSTOMER';
  customerAddress: string | null;
  notes: string | null;
  internalOrderNumber?: string | null;
  basePrice?: number;
  discountPercent?: number;
  finalPrice?: number;
  status: 'PENDING' | 'PARTS_ORDERED' | 'SHIPPED' | 'COMPLETED' | 'CANCELLED';
  createdAt: string;
  updatedAt: string;
  leftEyeSph?: number | null;
  leftEyeCyl?: number | null;
  leftEyeA?: number | null;
  rightEyeSph?: number | null;
  rightEyeCyl?: number | null;
  rightEyeA?: number | null;
  goggleModel?: {
    name: string;
    description: string | null;
  };
  dealer?: {
    id: string;
    username: string;
    email: string;
  };
}

export interface CreateOrderData {
  goggleModelId: string;
  glassType: string;
  bandType: string;
  productType: 'CLIP' | 'COMPLETE_GLASSES';
  deliveryType: 'SHOP' | 'CUSTOMER';
  customerAddress?: string;
  notes?: string;
  internalOrderNumber?: string;
  prescriptionImage?: File | null;
  leftEyeSph?: number | null;
  leftEyeCyl?: number | null;
  leftEyeA?: number | null;
  rightEyeSph?: number | null;
  rightEyeCyl?: number | null;
  rightEyeA?: number | null;
}

export interface CreateOrderResponse {
  order: {
    id: string;
    orderNumber: string;
    goggleModel: string;
    deliveryType: 'SHOP' | 'CUSTOMER';
    customerAddress: string | null;
    basePrice: number;
    discountPercent: number;
    finalPrice: number;
    status: 'PENDING' | 'COMPLETED';
    createdAt: string;
  };
}

export const getGoggleModels = async (): Promise<GoggleModel[]> => {
  const response = await api.get<GoggleModel[]>('/orders/models');
  return response.data;
};

export interface GlassOption {
  id: string;
  name: string;
  description?: string | null;
  active: boolean;
  createdAt: string;
}

export interface BandOption {
  id: string;
  name: string;
  description?: string | null;
  active: boolean;
  createdAt: string;
}

export const getGlassOptions = async (goggleModelId?: string): Promise<GlassOption[]> => {
  const params = goggleModelId ? { goggleModelId } : {};
  const response = await api.get<GlassOption[]>('/orders/glass-options', { params });
  return response.data;
};

export const getBandOptions = async (goggleModelId?: string): Promise<BandOption[]> => {
  const params = goggleModelId ? { goggleModelId } : {};
  const response = await api.get<BandOption[]>('/orders/band-options', { params });
  return response.data;
};

export const getProductTypes = async (goggleModelId: string): Promise<('CLIP' | 'COMPLETE_GLASSES')[]> => {
  const response = await api.get<('CLIP' | 'COMPLETE_GLASSES')[]>('/orders/product-types', {
    params: { goggleModelId },
  });
  return response.data;
};

export const createOrder = async (data: CreateOrderData): Promise<CreateOrderResponse> => {
  const formData = new FormData();
  formData.append('goggleModelId', data.goggleModelId);
  formData.append('glassType', data.glassType);
  formData.append('bandType', data.bandType);
  formData.append('productType', data.productType);
  formData.append('deliveryType', data.deliveryType);
  if (data.customerAddress) {
    formData.append('customerAddress', data.customerAddress);
  }
  if (data.notes) {
    formData.append('notes', data.notes);
  }
  if (data.internalOrderNumber) {
    formData.append('internalOrderNumber', data.internalOrderNumber);
  }
  if (data.prescriptionImage) {
    formData.append('prescriptionImage', data.prescriptionImage);
  }
  // Brillenpass-Werte (optional)
  if (data.leftEyeSph !== null && data.leftEyeSph !== undefined) {
    formData.append('leftEyeSph', String(data.leftEyeSph));
  }
  if (data.leftEyeCyl !== null && data.leftEyeCyl !== undefined) {
    formData.append('leftEyeCyl', String(data.leftEyeCyl));
  }
  if (data.leftEyeA !== null && data.leftEyeA !== undefined) {
    formData.append('leftEyeA', String(data.leftEyeA));
  }
  if (data.rightEyeSph !== null && data.rightEyeSph !== undefined) {
    formData.append('rightEyeSph', String(data.rightEyeSph));
  }
  if (data.rightEyeCyl !== null && data.rightEyeCyl !== undefined) {
    formData.append('rightEyeCyl', String(data.rightEyeCyl));
  }
  if (data.rightEyeA !== null && data.rightEyeA !== undefined) {
    formData.append('rightEyeA', String(data.rightEyeA));
  }

  const response = await api.post<CreateOrderResponse>('/orders', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getDealerOrders = async (): Promise<Order[]> => {
  const response = await api.get<Order[]>('/orders/my-orders');
  return response.data;
};

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export const getAllOrders = async (params?: {
  status?: string;
  dealerId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}): Promise<PaginatedResponse<Order>> => {
  const response = await api.get<PaginatedResponse<Order>>('/admin/orders', { params });
  return response.data;
};

export const getOrderById = async (id: string): Promise<Order> => {
  const response = await api.get<Order>(`/admin/orders/${id}`);
  return response.data;
};

export const updateOrderStatus = async (
  id: string,
  status: 'PENDING' | 'PARTS_ORDERED' | 'SHIPPED' | 'COMPLETED'
): Promise<Order> => {
  const response = await api.patch<Order>(`/admin/orders/${id}/status`, { status });
  return response.data;
};

export const confirmShipping = async (id: string): Promise<{ message: string }> => {
  const response = await api.post<{ message: string }>(`/admin/orders/${id}/confirm-shipping`);
  return response.data;
};

export const cancelOrder = async (
  id: string,
  isAdmin: boolean = false
): Promise<{ message: string; order: Order }> => {
  const endpoint = isAdmin ? `/admin/orders/${id}/cancel` : `/orders/${id}/cancel`;
  const response = await api.post<{ message: string; order: Order }>(endpoint);
  return response.data;
};

export const createInvoiceFromOrder = async (
  orderId: string
): Promise<{
  success: boolean;
  message: string;
  invoice: { id: string; invoiceNumber: string };
}> => {
  const response = await api.post<{
    success: boolean;
    message: string;
    invoice: { id: string; invoiceNumber: string };
  }>(`/admin/orders/${orderId}/create-invoice`);
  return response.data;
};
